/**
 * Картотека — the firm's book of people: every customer it knows, and one customer's card.
 *
 * Not Клиенти. That report ranks a period's riders by what they rode and paid; this one has no
 * period and ranks nothing. It is where somebody looks a person up — by number, name or an
 * address — and sees who they are and every address they are known to go to. It exists above
 * all for the callers an import brought: thousands of numbers with addresses and no ride here
 * yet, whom no ranking of rides would ever show, marked Внесен and filtered on their own.
 *
 * The list is the server's, a page at a time, because a firm's book is thousands long. The
 * card is one request, and its two orders of addresses — most recent, most used — are the same
 * list sorted here, since a person has tens of addresses and not thousands.
 *
 * The chosen customer is in the address, `?customer=…`, so a card can be sent to a colleague
 * and survives a reload. An order on the card opens its journey.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { describe } from '../settings/errors';
import { useTenantFormats } from '../shell/formats';
import {
  Card,
  CardHeader,
  Count,
  EmptyState,
  Input,
  KeyValue,
  KeyValueRow,
  PageHeader,
  Pager,
  Pill,
  Segmented,
  Sub,
  Table,
  Td,
  Th,
  Tr,
} from '../ui';
import {
  reportsApi,
  type CustomerCardView,
  type DirectoryOrigin,
  type DirectoryPage as Page,
  type DirectorySort,
} from './api';
import type { ReportPageProps } from './frame';

/** A Bulgarian number written the way it is said, `0888 123 456`; anything else as stored. */
export function spokenNumber(e164: string): string {
  if (!e164.startsWith('+359')) return e164;

  const national = '0' + e164.slice(4);

  return national.length === 10
    ? `${national.slice(0, 4)} ${national.slice(4, 7)} ${national.slice(7)}`
    : national;
}

type AddressOrder = 'recent' | 'used';

export function DirectoryPage({ frame }: ReportPageProps) {
  const { t } = useTranslation();
  const formats = useTenantFormats();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [typed, setTyped] = useState('');
  const [query, setQuery] = useState('');
  const [origin, setOrigin] = useState<DirectoryOrigin>('All');
  const [sort, setSort] = useState<DirectorySort>('Recent');
  const [page, setPage] = useState(0);
  const [list, setList] = useState<Page | null>(null);

  const chosen = params.get('customer');
  const [card, setCard] = useState<CustomerCardView | null>(null);
  const [addressOrder, setAddressOrder] = useState<AddressOrder>('recent');

  const { fail } = frame;

  // A pause in the typing, not every key, asks the server.
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(typed.trim());
      setPage(0);
    }, 300);

    return () => clearTimeout(timer);
  }, [typed]);

  useEffect(() => {
    let live = true;

    reportsApi
      .directory(query, origin, sort, page)
      .then((answer) => {
        if (live) {
          setList(answer);
          fail(null);
        }
      })
      .catch((error: unknown) => live && fail(describe(error, t, 'settings.couldNotLoad')));

    return () => {
      live = false;
    };
  }, [query, origin, sort, page, fail, t]);

  useEffect(() => {
    if (chosen === null) {
      setCard(null);
      return;
    }

    let live = true;

    reportsApi
      .customerCard(chosen)
      .then((answer) => live && setCard(answer))
      .catch((error: unknown) => live && fail(describe(error, t, 'settings.couldNotLoad')));

    return () => {
      live = false;
    };
  }, [chosen, fail, t]);

  const choose = (customerId: string) => {
    const next = new URLSearchParams(params);
    next.set('customer', customerId);
    setParams(next, { replace: true });
  };

  const addresses = useMemo(() => {
    const all = [...(card?.customer.addresses ?? [])];

    return addressOrder === 'used'
      ? all.sort((a, b) => b.useCount - a.useCount || b.lastUsedAt.localeCompare(a.lastUsedAt))
      : all.sort((a, b) => b.lastUsedAt.localeCompare(a.lastUsedAt) || b.useCount - a.useCount);
  }, [card, addressOrder]);

  return (
    <>
      <PageHeader title={t('nav.reports.directory')} description={t('reports.about.directory')} />

      <div className="flex flex-wrap items-center gap-3">
        {/* The width on a wrapper: every control is full width, and a second width class on
            the input itself would be settled by Tailwind's order rather than by this line. */}
        <div className="w-72">
          <Input
            type="search"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            placeholder={t('reports.directory.search')}
            aria-label={t('reports.directory.search')}
          />
        </div>
        <Segmented
          label={t('reports.directory.origin')}
          value={origin}
          compact
          options={[
            { value: 'All', label: t('reports.directory.everybody') },
            { value: 'Imported', label: t('reports.directory.imported') },
            { value: 'Here', label: t('reports.directory.here') },
          ]}
          onChange={(value) => {
            setOrigin(value);
            setPage(0);
          }}
        />
        <Segmented
          label={t('reports.directory.order')}
          value={sort}
          compact
          options={[
            { value: 'Recent', label: t('reports.directory.recent') },
            { value: 'MostUsed', label: t('reports.directory.mostUsed') },
          ]}
          onChange={(value) => {
            setSort(value);
            setPage(0);
          }}
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] items-start">
        <Card>
          <CardHeader
            title={t('reports.directory.clients')}
            actions={
              list !== null && (
                <Pager page={page} pageSize={list.pageSize} total={list.total} count={formats.count} onPage={setPage} />
              )
            }
          >
            {list !== null && <Count>{formats.count(list.total)}</Count>}
          </CardHeader>

          {list !== null &&
            (list.rows.length === 0 ? (
              <EmptyState>{t('reports.directory.none')}</EmptyState>
            ) : (
              <Table>
                <thead>
                  <Tr>
                    <Th>{t('reports.directory.client')}</Th>
                    <Th numeric>{t('reports.directory.addresses')}</Th>
                    <Th numeric>{t('reports.directory.uses')}</Th>
                    <Th>{t('reports.directory.lastUsed')}</Th>
                    <Th />
                  </Tr>
                </thead>
                <tbody>
                  {list.rows.map((row) => (
                    <Tr
                      key={row.customerId}
                      selected={row.customerId === chosen}
                      onClick={() => choose(row.customerId)}
                    >
                      <Td>
                        <span className="tabular-nums">{spokenNumber(row.phoneNumber)}</span>
                        {row.displayName !== null && <Sub block>{row.displayName}</Sub>}
                      </Td>
                      <Td numeric>{formats.count(row.addresses)}</Td>
                      <Td numeric>{formats.count(row.uses)}</Td>
                      <Td>{row.lastUsedAt === null ? '—' : formats.moment(row.lastUsedAt)}</Td>
                      <Td>
                        <div className="flex flex-wrap gap-1">
                          {row.importedAt !== null && (
                            <Pill tone="info" soft>
                              {t('reports.directory.importedOne')}
                            </Pill>
                          )}
                          {row.hasAccount && <Pill soft>{t('reports.directory.app')}</Pill>}
                          {row.isBlocked && (
                            <Pill tone="danger" soft>
                              {t('reports.directory.blocked')}
                            </Pill>
                          )}
                        </div>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            ))}
        </Card>

        {card === null ? (
          <Card>
            <EmptyState>{t('reports.directory.choose')}</EmptyState>
          </Card>
        ) : (
          <div className="flex flex-col gap-4">
            <Card>
              <CardHeader title={spokenNumber(card.customer.phoneNumber)}>
                {card.customer.importedAt !== null && (
                  <Pill tone="info" soft>
                    {t('reports.directory.importedOne')}
                  </Pill>
                )}
                {card.customer.isBlocked && (
                  <Pill tone="danger" soft>
                    {t('reports.directory.blocked')}
                  </Pill>
                )}
              </CardHeader>
              <KeyValue className="px-3.5 py-3">
                <KeyValueRow label={t('reports.directory.name')}>
                  {card.customer.displayName ?? '—'}
                </KeyValueRow>
                {card.customer.alternatePhones.length > 0 && (
                  <KeyValueRow label={t('reports.directory.otherNumbers')}>
                    {card.customer.alternatePhones.map(spokenNumber).join(', ')}
                  </KeyValueRow>
                )}
                {card.customer.notes !== null && (
                  <KeyValueRow label={t('reports.directory.notes')}>{card.customer.notes}</KeyValueRow>
                )}
                <KeyValueRow label={t('reports.directory.knownSince')}>
                  {card.customer.importedAt !== null
                    ? t('reports.directory.importedOn', { date: formats.moment(card.customer.importedAt) })
                    : formats.moment(card.customer.createdAt)}
                </KeyValueRow>
                <KeyValueRow label={t('reports.directory.rides')}>
                  {card.orders.total === 0
                    ? t('reports.directory.noOrders')
                    : t('reports.directory.orderCounts', {
                        total: formats.count(card.orders.total),
                        completed: formats.count(card.orders.completed),
                        cancelled: formats.count(card.orders.cancelled),
                      })}
                </KeyValueRow>
                {card.orders.lastRideAt !== null && (
                  <KeyValueRow label={t('reports.directory.lastRide')}>
                    {formats.moment(card.orders.lastRideAt)}
                  </KeyValueRow>
                )}
                <KeyValueRow label={t('reports.directory.app')}>
                  {card.customer.hasAccount ? t('reports.directory.yes') : t('reports.directory.no')}
                </KeyValueRow>
              </KeyValue>
            </Card>

            <Card>
              <CardHeader
                title={t('reports.directory.knownAddresses')}
                actions={
                  <Segmented
                    label={t('reports.directory.order')}
                    value={addressOrder}
                    compact
                    options={[
                      { value: 'recent', label: t('reports.directory.recent') },
                      { value: 'used', label: t('reports.directory.mostUsed') },
                    ]}
                    onChange={setAddressOrder}
                  />
                }
              >
                <Count>{formats.count(addresses.length)}</Count>
              </CardHeader>
              {addresses.length === 0 ? (
                <EmptyState>{t('reports.directory.noAddresses')}</EmptyState>
              ) : (
                <Table>
                  <thead>
                    <Tr>
                      <Th>{t('reports.directory.address')}</Th>
                      <Th numeric>{t('reports.directory.uses')}</Th>
                      <Th>{t('reports.directory.lastUsed')}</Th>
                      <Th />
                    </Tr>
                  </thead>
                  <tbody>
                    {addresses.map((address) => (
                      <Tr key={address.addressId}>
                        <Td>
                          {address.label !== null && <span className="font-semibold">{address.label} · </span>}
                          {address.placeLine ?? address.address}
                          {address.placeLine !== null && address.placeLine !== address.address && (
                            <Sub block>{t('reports.directory.typedAs', { address: address.address })}</Sub>
                          )}
                          {address.notes !== null && <Sub block>{address.notes}</Sub>}
                        </Td>
                        <Td numeric>{formats.count(address.useCount)}</Td>
                        <Td>{formats.moment(address.lastUsedAt)}</Td>
                        <Td>
                          <div className="flex flex-wrap gap-1">
                            {address.placeId !== null ? (
                              <Pill tone="ok" soft>
                                {t('reports.directory.locked')}
                              </Pill>
                            ) : address.longitude === null ? (
                              <Pill tone="warn" soft>
                                {t('reports.directory.noPoint')}
                              </Pill>
                            ) : null}
                            {address.importedAt !== null && (
                              <Pill tone="info" soft>
                                {t('reports.directory.importedOne')}
                              </Pill>
                            )}
                          </div>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>

            <Card>
              <CardHeader title={t('reports.directory.recentOrders')}>
                <Count>{formats.count(card.orders.recent.length)}</Count>
              </CardHeader>
              {card.orders.recent.length === 0 ? (
                <EmptyState>{t('reports.directory.noOrders')}</EmptyState>
              ) : (
                <Table>
                  <thead>
                    <Tr>
                      <Th>{t('reports.directory.when')}</Th>
                      <Th>{t('reports.directory.route')}</Th>
                      <Th>{t('reports.directory.state')}</Th>
                      <Th numeric>{t('reports.directory.fare')}</Th>
                    </Tr>
                  </thead>
                  <tbody>
                    {card.orders.recent.map((order) => (
                      <Tr key={order.orderId} onClick={() => navigate(`/reports/journeys?order=${order.orderId}`)}>
                        <Td>
                          {formats.moment(order.requestedAt)}
                          <Sub block>{order.reference}</Sub>
                        </Td>
                        <Td>
                          {order.pickup}
                          {order.destination !== null && <Sub block>{order.destination}</Sub>}
                        </Td>
                        <Td>{t(`states.${order.state}`)}</Td>
                        <Td numeric>{order.fare === null ? '—' : formats.money(order.fare)}</Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>
          </div>
        )}
      </div>
    </>
  );
}
